import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { Typography } from '@kep-ui-kit/ui-components';
import { InstructionsSentContainerV2 } from '../ui/instructions-sent-container-v2';
import { ForgotPasswordContainerV2 } from '../ui/forgot-password-container-v2';
import { withErrorHandling } from '@selfserve/react-hocs';
import {
  StyledLogo,
  StyledV2PageWrapper,
  StyledV2LogoRow,
  StyledV2PageBackground,
  StyledV2GlassCard,
} from '@selfserve/react-ui';
import { apiStatuses, getRecaptchaToken } from '@selfserve/react-utils';
import { useForgotPasswordRequest } from '../data';

const StyledGlassCard = styled(StyledV2GlassCard)({
  maxWidth: '600px',
});

const StyledTitleBlock = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  textAlign: 'center',
  color: theme.palette.text.primary,
}));

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('common.validations.email-valid'))
      .required(t('common.validations.email-required')),
  });

export interface FormValues {
  email: string;
}

export const ForgotPasswordV2 = withErrorHandling(({ openErrorModal }) => {
  const { t, i18n } = useTranslation();
  const [validateOnChange, setValidateOnChange] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isInProccess, setIsInProccess] = useState(false);
  const {
    data: forgotPasswordResponse,
    mutateAsync: handleForgotPasswordRequest,
    isLoading,
  } = useForgotPasswordRequest();
  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnChange,
    initialValues: { email: '' },
    validationSchema: useMemo(() => validationSchema(t), [t]),
    onSubmit: ({ email: userEmail }) => {
      setIsInProccess(true);
      getRecaptchaToken()
        .then((recaptchaToken: string) => {
          return handleForgotPasswordRequest({
            userEmail,
            recaptchaToken,
            postResetLink: window.location.origin,
          }).catch(() => {
            setIsInProccess(false);
            openErrorModal();
          });
        })
        .catch(() => {
          setIsInProccess(false);
          openErrorModal();
        });
    },
  });
  const { errors, setFieldError } = formik;

  useEffect(() => {
    const hasError = !!Object.keys(errors).length;
    if (hasError && !validateOnChange) {
      setValidateOnChange(true);
    } else if (!hasError) {
      setValidateOnChange(false);
    }
  }, [validateOnChange, errors]);

  useEffect(() => {
    const { data, status } = forgotPasswordResponse || {};
    if (status) {
      setIsInProccess(false);
      if (status === apiStatuses.BAD_REQUEST) {
        setFieldError(
          'email',
          i18n.exists(`errors.${data?.id}`)
            ? t(`errors.${data?.id}`)
            : data?.message_en
        );
      }
      if (status === apiStatuses.CREATED) {
        setIsSubmitted(true);
      }
    }
  }, [forgotPasswordResponse]);

  return (
    <StyledV2PageWrapper>
      <StyledV2LogoRow>
        <StyledLogo alt="Kaltura" />
      </StyledV2LogoRow>
      <StyledV2PageBackground centered className="container">
        <StyledGlassCard>
          {isSubmitted ? (
            <StyledTitleBlock>
              <Typography variant="h2" textAlign="center">
                {t('common.forgot-password-instructions-sent')}
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                <img src="assets/checkbox.svg" alt="" style={{ width: '24px', height: '24px' }} />
                <Typography variant={'body1Highlight' as any}>
                  {t('common.forgot-password-email-sent')}
                </Typography>
              </div>
              <Typography variant="body1" textAlign="center">
                {t('common.forgot-password-message')}
              </Typography>
            </StyledTitleBlock>
          ) : (
            <StyledTitleBlock>
              <Typography variant="h2" textAlign="center">
                {t('common.reset-password')}
              </Typography>
              <Typography variant="body1" textAlign="center">
                {t('common.forgot-password-instructions')}
              </Typography>
            </StyledTitleBlock>
          )}
          {isSubmitted ? (
            <InstructionsSentContainerV2 />
          ) : (
            <ForgotPasswordContainerV2
              formik={formik}
              isLoading={isLoading || isInProccess}
            />
          )}
        </StyledGlassCard>
      </StyledV2PageBackground>
    </StyledV2PageWrapper>
  );
});
